/**
 * @voltxampere/web — `<vxa-app>` root container (Sprint 11).
 *
 * Owns the topbar / layout / statusbar grid plus the modal
 * teleport targets (command palette, shortcut overlay,
 * generic modal root, toast stack). Light DOM is empty by
 * design — every child is a registered custom element.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, html, css } from "lit";
import { customElement } from "lit/decorators.js";

import "./vxa-topbar.js";
import "./vxa-pane-router.js";
import "./vxa-statusbar.js";
import "./vxa-command-palette.js";
import "./vxa-shortcut-overlay.js";
import "./vxa-modal-root.js";
import "./vxa-toast-stack.js";
import "./vxa-import-dialog.js";
import "./vxa-export-dialog.js";
// Sprint 20 — Bode plot custom element registration.
// Sprint 21 C8 — auto-mount via vxa-layout (currentACResult signal
// gate); component is also mountable manually for ad-hoc smoke.
import "./vxa-bode-plot.js";
// Sprint 21 C7 — AC config dialog (F6 modal, Sprint 20 C6 deferred).
import "./vxa-ac-config-dialog.js";
// Sprint 56c — ERC violations panel (VISION §6.F P0).
import "./vxa-erc-panel.js";
// Sprint 56b — Holographic formula overlay (VISION §6.G P0).
import "./vxa-formula-overlay.js";
// Sprint 57 — Karakter polish overlay (snap glow + hover tooltip).
import "./vxa-polish-overlay.js";

@customElement("vxa-app")
export class VxaApp extends LitElement {
  static override styles = css`
    :host {
      display: grid;
      grid-template-rows: var(--topbar-height) 1fr var(--statusbar-height);
      height: 100%;
      width: 100%;
      background: var(--void);
      color: var(--ivory);
      overflow: hidden;
    }
  `;

  override render() {
    return html`
      <vxa-topbar></vxa-topbar>
      <vxa-pane-router></vxa-pane-router>
      <vxa-statusbar></vxa-statusbar>
      <vxa-command-palette></vxa-command-palette>
      <vxa-shortcut-overlay></vxa-shortcut-overlay>
      <vxa-modal-root></vxa-modal-root>
      <vxa-toast-stack></vxa-toast-stack>
      <vxa-import-dialog></vxa-import-dialog>
      <vxa-export-dialog></vxa-export-dialog>
      <vxa-ac-config-dialog></vxa-ac-config-dialog>
      <vxa-erc-panel></vxa-erc-panel>
      <vxa-formula-overlay></vxa-formula-overlay>
      <vxa-polish-overlay></vxa-polish-overlay>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-app": VxaApp;
  }
}
